import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertCircle, Plus, X, Clock, CheckCircle, XCircle, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  OPEN: { bg: 'rgba(75, 157, 255, 0.1)', color: '#4B9DFF', label: 'Open' },
  UNDER_REVIEW: { bg: 'rgba(255, 193, 7, 0.1)', color: '#FFC107', label: 'Under Review' },
  RESOLVED: { bg: 'rgba(71, 255, 176, 0.1)', color: '#47FFB0', label: 'Resolved' },
  REJECTED: { bg: 'rgba(255, 107, 107, 0.1)', color: '#FF6B6B', label: 'Rejected' },
};

const DISPUTE_TYPES = ['UTILITY', 'PAYOUT', 'TRANSFER', 'MARKETPLACE', 'OTHER'];

const BuyerDisputesPage = () => {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [nfts, setNfts] = useState([]);
  const [transfers, setTransfers] = useState([]);

  const [form, setForm] = useState({
    dispute_type: 'OTHER',
    subject: '',
    description: '',
    portfolio_id: '',
    nft_id: '',
    transfer_id: '',
  });

  useEffect(() => { fetchDisputes(); }, []);

  const fetchDisputes = async () => {
    try {
      const res = await axios.get(`${API}/buyer/disputes`);
      setDisputes(res.data || []);
    } catch (err) {
      console.error('Failed to load disputes:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = async () => {
    setModalOpen(true);
    try {
      const [nftRes, trRes] = await Promise.all([
        axios.get(`${API}/buyer/nfts`).catch(() => ({ data: [] })),
        axios.get(`${API}/buyer/transfers`).catch(() => ({ data: { transfers: [] } })),
      ]);
      setNfts(Array.isArray(nftRes.data) ? nftRes.data : nftRes.data.nfts || []);
      setTransfers(Array.isArray(trRes.data) ? trRes.data : trRes.data.transfers || []);
    } catch (e) { /* ignore */ }
  };

  const handleSubmit = async () => {
    if (form.subject.trim().length < 3) return toast.error('Subject must be at least 3 characters');
    if (form.description.trim().length < 10) return toast.error('Description must be at least 10 characters');
    setSubmitting(true);
    try {
      const payload = {
        dispute_type: form.dispute_type,
        subject: form.subject.trim(),
        description: form.description.trim(),
      };
      if (form.portfolio_id) payload.portfolio_id = form.portfolio_id;
      if (form.nft_id) payload.nft_id = form.nft_id;
      if (form.transfer_id) payload.transfer_id = form.transfer_id;

      await axios.post(`${API}/buyer/disputes`, payload);
      toast.success('Dispute filed successfully');
      setModalOpen(false);
      setForm({ dispute_type: 'OTHER', subject: '', description: '', portfolio_id: '', nft_id: '', transfer_id: '' });
      fetchDisputes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to file dispute');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', background: '#FFFFFF',
    border: '1px solid #E6E8EF', borderRadius: '8px', color: '#0B0B0B', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box',
  };

  const uniquePortfolios = [...new Map(nfts.filter(n => n.portfolio_id).map(n => [n.portfolio_id, { id: n.portfolio_id, name: n.portfolio_name }])).values()];

  if (loading) return <div style={{ padding: '40px', color: '#999' }}>Loading disputes...</div>;

  return (
    <div data-testid="buyer-disputes-page" style={{ padding: '40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#0B0B0B', marginBottom: '8px' }}>Disputes</h1>
          <p style={{ fontSize: '16px', color: '#999' }}>Manage and track your dispute tickets</p>
        </div>
        <button
          data-testid="file-new-dispute-btn"
          onClick={openModal}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '12px 24px', background: '#4B9DFF', color: '#FFF',
            border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          }}
        >
          <Plus size={16} /> File New Dispute
        </button>
      </div>

      {disputes.length === 0 ? (
        <div style={{ background: '#FFFFFF', borderRadius: '16px', padding: '60px', textAlign: 'center', border: '1px solid #E6E8EF' }}>
          <AlertCircle size={48} color="#47FFB0" style={{ marginBottom: '16px' }} />
          <h3 style={{ color: '#0B0B0B', fontSize: '20px', marginBottom: '8px' }}>No Active Disputes</h3>
          <p style={{ color: '#666', fontSize: '14px', maxWidth: '400px', margin: '0 auto' }}>
            You don't have any open disputes. Click "File New Dispute" above to submit one.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {disputes.map(d => {
            const st = STATUS_STYLES[d.status] || STATUS_STYLES.OPEN;
            return (
              <div key={d.id} data-testid={`dispute-card-${d.id}`} style={{
                background: '#FFFFFF', border: '1px solid #E6E8EF', borderRadius: '12px', padding: '20px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div>
                    <h3 style={{ color: '#0B0B0B', fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>{d.subject}</h3>
                    <p style={{ color: '#666', fontSize: '13px' }}>
                      {d.dispute_type} {d.portfolio_name ? `| ${d.portfolio_name}` : ''}
                    </p>
                  </div>
                  <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: st.bg, color: st.color }}>
                    {st.label}
                  </span>
                </div>
                <p style={{ color: '#999', fontSize: '14px', marginBottom: '8px', lineHeight: '1.5' }}>
                  {d.description.length > 200 ? d.description.slice(0, 200) + '...' : d.description}
                </p>
                <p style={{ color: '#555', fontSize: '12px' }}>
                  Filed: {new Date(d.created_at).toLocaleDateString()}
                  {d.admin_notes ? ` | Admin: ${d.admin_notes.slice(0, 60)}` : ''}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* File Dispute Modal */}
      {modalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div data-testid="dispute-modal" style={{
            background: '#FFFFFF', borderRadius: '16px', padding: '32px', width: '520px', maxHeight: '85vh', overflowY: 'auto',
            border: '1px solid #E6E8EF',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ color: '#0B0B0B', fontSize: '20px', fontWeight: 700 }}>File New Dispute</h2>
              <button onClick={() => setModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} color="#999" />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#999', fontSize: '13px', marginBottom: '6px' }}>Dispute Type</label>
                <select data-testid="dispute-type-select" value={form.dispute_type} onChange={e => setForm(f => ({ ...f, dispute_type: e.target.value }))} style={inputStyle}>
                  {DISPUTE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', color: '#999', fontSize: '13px', marginBottom: '6px' }}>Subject</label>
                <input data-testid="dispute-subject-input" type="text" value={form.subject} maxLength={120}
                  onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Brief summary..." style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', color: '#999', fontSize: '13px', marginBottom: '6px' }}>Description</label>
                <textarea data-testid="dispute-description-input" value={form.description} maxLength={2000} rows={4}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the issue in detail..."
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '100px' }} />
              </div>
              {uniquePortfolios.length > 0 && (
                <div>
                  <label style={{ display: 'block', color: '#999', fontSize: '13px', marginBottom: '6px' }}>Portfolio (optional)</label>
                  <select data-testid="dispute-portfolio-select" value={form.portfolio_id} onChange={e => setForm(f => ({ ...f, portfolio_id: e.target.value }))} style={inputStyle}>
                    <option value="">-- Select Portfolio --</option>
                    {uniquePortfolios.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              <button
                data-testid="dispute-submit-btn"
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  padding: '12px', background: submitting ? '#666' : '#4B9DFF', color: '#FFF',
                  border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600,
                  cursor: submitting ? 'not-allowed' : 'pointer', marginTop: '8px',
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Dispute'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyerDisputesPage;
